import React, { useState, useEffect } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer } from 'recharts';
import { DollarSign, Trash2, Download, Calculator } from 'lucide-react';
import { toast } from 'react-toastify';
import axios from 'axios';

const BudgetPlanner = ({ tripId }) => {
  const [budget, setBudget] = useState(null);
  const [totalBudget, setTotalBudget] = useState('');
  const [newExpense, setNewExpense] = useState({
    category: 'Food',
    description: '',
    amount: '',
    day: 1
  });

  const API_URL = process.env.REACT_APP_API_URL;
  
  const getAuthConfig = () => {
    const user = JSON.parse(localStorage.getItem('user'));
    return { headers: { Authorization: `Bearer ${user?.token}` } };
  };

  const categories = ['Food', 'Travel', 'Stay', 'Activities', 'Shopping', 'Other'];
  const categoryColors = {
    Food: '#FF6B6B',
    Travel: '#4ECDC4',
    Stay: '#45B7D1',
    Activities: '#96CEB4',
    Shopping: '#FFEAA7',
    Other: '#DDA0DD'
  };

  const fetchBudget = async () => {
    try {
      const response = await axios.get(`${API_URL}/budget/${tripId}`, getAuthConfig());
      setBudget(response.data);
      setTotalBudget(response.data.totalBudget);
    } catch (error) {
      if (error.response?.status !== 404) {
        toast.error('Failed to fetch budget');
      }
    }
  };

  useEffect(() => {
    fetchBudget();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tripId]);

  const createBudget = async () => {
    try {
      const response = await axios.post(`${API_URL}/budget/${tripId}`, {
        totalBudget: parseFloat(totalBudget)
      }, getAuthConfig());
      setBudget(response.data);
      toast.success('💰 Budget created successfully!');
    } catch (error) {
      toast.error('Failed to create budget');
    }
  };

  const addExpense = async () => {
    try {
      const response = await axios.post(`${API_URL}/budget/${tripId}/expenses`, {
        ...newExpense,
        amount: parseFloat(newExpense.amount)
      }, getAuthConfig());
      setBudget(response.data);
      setNewExpense({ category: 'Food', description: '', amount: '', day: 1 });
      toast.success('💸 Expense added!');
    } catch (error) {
      toast.error('Failed to add expense');
    }
  };

  const deleteExpense = async (expenseId) => {
    try {
      const response = await axios.delete(`${API_URL}/budget/${tripId}/expenses/${expenseId}`, getAuthConfig());
      setBudget(response.data);
      toast.success('🗑️ Expense deleted!');
    } catch (error) {
      toast.error('Failed to delete expense');
    }
  };

  const exportToPDF = () => {
    // Simple CSV export for now
    const csvContent = [
      ['Category', 'Description', 'Amount', 'Day'],
      ...budget.expenses.map(exp => [exp.category, exp.description, exp.amount, exp.day])
    ].map(row => row.join(',')).join('\\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `trip-budget-${tripId}.csv`;
    a.click();
    toast.success('📊 Budget exported!');
  };

  const pieData = budget ? Object.entries(budget.categoryTotals || {}).map(([category, amount]) => ({
    name: category,
    value: amount,
    color: categoryColors[category]
  })) : [];

  if (!budget) {
    return (
      <div className="bg-gradient-to-r from-green-400 to-blue-500 rounded-3xl p-8 text-white animate-bounce-in">
        <div className="text-center">
          <DollarSign className="h-16 w-16 mx-auto mb-4" />
          <h3 className="text-2xl font-bold mb-4">💰 Set Your Trip Budget</h3>
          <div className="max-w-md mx-auto">
            <input
              type="number"
              value={totalBudget}
              onChange={(e) => setTotalBudget(e.target.value)}
              placeholder="Enter total budget"
              className="w-full px-4 py-3 rounded-xl text-gray-900 mb-4"
            />
            <button onClick={createBudget} className="btn-primary w-full">
              🚀 Create Budget
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Budget Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-gradient-to-r from-green-400 to-emerald-500 rounded-2xl p-6 text-white shadow-xl">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-100">💰 Total Budget</p>
              <p className="text-3xl font-bold">${budget.totalBudget}</p>
            </div>
            <Calculator className="h-12 w-12 text-green-200" />
          </div>
        </div>
        
        <div className="bg-gradient-to-r from-red-400 to-pink-500 rounded-2xl p-6 text-white shadow-xl">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-red-100">💸 Total Spent</p>
              <p className="text-3xl font-bold">${budget.totalExpenses}</p>
            </div>
            <DollarSign className="h-12 w-12 text-red-200" />
          </div>
        </div>
        
        <div className={`bg-gradient-to-r ${budget.remaining >= 0 ? 'from-blue-400 to-purple-500' : 'from-orange-400 to-red-500'} rounded-2xl p-6 text-white shadow-xl`}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-100">{budget.remaining >= 0 ? '💎 Remaining' : '⚠️ Over Budget'}</p>
              <p className="text-3xl font-bold">${Math.abs(budget.remaining)}</p>
            </div>
            <div className="text-4xl">{budget.remaining >= 0 ? '😊' : '😰'}</div>
          </div>
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Pie Chart */}
        <div className="bg-white rounded-3xl p-6 shadow-xl border border-gray-100">
          <h3 className="text-xl font-bold mb-4 text-center">📊 Spending by Category</h3>
          {pieData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  outerRadius={100}
                  dataKey="value"
                  label={({name, value}) => `${name}: $${value}`}
                >
                  {pieData.map((entry, index) => (
                    <Cell key={index} fill={entry.color} />
                  ))}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="text-center py-12 text-gray-500">
              <div className="text-6xl mb-4">📈</div>
              <p>No expenses yet. Add some to see the breakdown!</p>
            </div>
          )}
        </div>

        {/* Add Expense Form */}
        <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-3xl p-6 shadow-xl border border-purple-100">
          <h3 className="text-xl font-bold mb-4 text-center">➕ Add New Expense</h3>
          <div className="space-y-4">
            <select
              value={newExpense.category}
              onChange={(e) => setNewExpense({...newExpense, category: e.target.value})}
              className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-purple-500"
            >
              {categories.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
            
            <input
              type="text"
              placeholder="Description (e.g., Lunch at cafe)"
              value={newExpense.description}
              onChange={(e) => setNewExpense({...newExpense, description: e.target.value})}
              className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-purple-500"
            />
            
            <div className="grid grid-cols-2 gap-4">
              <input
                type="number"
                placeholder="Amount"
                value={newExpense.amount}
                onChange={(e) => setNewExpense({...newExpense, amount: e.target.value})}
                className="px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-purple-500"
              />
              <input
                type="number"
                placeholder="Day"
                value={newExpense.day}
                onChange={(e) => setNewExpense({...newExpense, day: e.target.value})}
                className="px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-purple-500"
              />
            </div>
            
            <button onClick={addExpense} className="w-full btn-success">
              💰 Add Expense
            </button>
          </div>
        </div>
      </div>

      {/* Expenses List */}
      <div className="bg-white rounded-3xl p-6 shadow-xl border border-gray-100">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl font-bold">📋 Expense History</h3>
          <button onClick={exportToPDF} className="btn-secondary flex items-center space-x-2">
            <Download className="h-4 w-4" />
            <span>📊 Export CSV</span>
          </button>
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-3 px-4">Category</th>
                <th className="text-left py-3 px-4">Description</th>
                <th className="text-left py-3 px-4">Amount</th>
                <th className="text-left py-3 px-4">Day</th>
                <th className="text-left py-3 px-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {budget.expenses.map((expense, index) => (
                <tr key={expense._id} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="py-3 px-4">
                    <span 
                      className="px-3 py-1 rounded-full text-white text-sm font-medium"
                      style={{ backgroundColor: categoryColors[expense.category] }}
                    >
                      {expense.category}
                    </span>
                  </td>
                  <td className="py-3 px-4">{expense.description}</td>
                  <td className="py-3 px-4 font-semibold">${expense.amount}</td>
                  <td className="py-3 px-4">Day {expense.day}</td>
                  <td className="py-3 px-4">
                    <button
                      onClick={() => deleteExpense(expense._id)}
                      className="text-red-500 hover:text-red-700 p-2 rounded-lg hover:bg-red-50 transition-colors"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          
          {budget.expenses.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              <div className="text-6xl mb-4">💳</div>
              <p>No expenses recorded yet. Start tracking your spending!</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BudgetPlanner;